import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ITokenUsageTooltipProps } from './TokenUsageTooltip.type';
import { formatCost } from '../../utils/formatCost';

const formatRoundedDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

type TooltipPosition = {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
  arrowOffset: number;
};

export const TokenUsageTooltip: React.FC<ITokenUsageTooltipProps> = ({ 
  isOpen, 
  onClose, 
  tokenUsage, 
  developmentTiming,
  brainstormTiming,
  triggerRef
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  // Calculate optimal position for tooltip
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current) return;

    const calculatePosition = () => {
      const triggerRect = triggerRef.current!.getBoundingClientRect();
      const tooltipRect = tooltipRef.current!.getBoundingClientRect();
      
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const SPACING = 12; // Space between trigger and tooltip
      const ARROW_SIZE = 6;
      
      // Check if trigger is out of viewport - if so, close the tooltip
      const isTriggerOutOfViewport = 
        triggerRect.bottom < 0 || 
        triggerRect.top > viewportHeight ||
        triggerRect.right < 0 || 
        triggerRect.left > viewportWidth;
      
      if (isTriggerOutOfViewport) {
        onClose();
        return;
      }
      
      let top = 0;
      let left = 0;
      let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'left';
      let arrowOffset = 0;

      // Try positioning to the right first (preferred)
      if (triggerRect.right + SPACING + tooltipRect.width <= viewportWidth) {
        left = triggerRect.right + SPACING + ARROW_SIZE;
        top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        arrowPosition = 'left';
        arrowOffset = tooltipRect.height / 2;
        
        // Adjust if tooltip goes off top or bottom
        if (top < SPACING) {
          arrowOffset = arrowOffset + top - SPACING;
          top = SPACING;
        } else if (top + tooltipRect.height > viewportHeight - SPACING) {
          arrowOffset = arrowOffset + (top + tooltipRect.height - (viewportHeight - SPACING));
          top = viewportHeight - SPACING - tooltipRect.height;
        }
      }
      // Try positioning to the left
      else if (triggerRect.left - SPACING - tooltipRect.width >= 0) {
        left = triggerRect.left - SPACING - tooltipRect.width - ARROW_SIZE;
        top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        arrowPosition = 'right';
        arrowOffset = tooltipRect.height / 2;
        
        // Adjust if tooltip goes off top or bottom
        if (top < SPACING) {
          arrowOffset = arrowOffset + top - SPACING;
          top = SPACING;
        } else if (top + tooltipRect.height > viewportHeight - SPACING) {
          arrowOffset = arrowOffset + (top + tooltipRect.height - (viewportHeight - SPACING));
          top = viewportHeight - SPACING - tooltipRect.height;
        }
      }
      // Try positioning below
      else if (triggerRect.bottom + SPACING + tooltipRect.height <= viewportHeight) {
        top = triggerRect.bottom + SPACING + ARROW_SIZE;
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        arrowPosition = 'top';
        arrowOffset = tooltipRect.width / 2;
        
        // Adjust if tooltip goes off left or right
        if (left < SPACING) {
          arrowOffset = arrowOffset + left - SPACING;
          left = SPACING;
        } else if (left + tooltipRect.width > viewportWidth - SPACING) {
          arrowOffset = arrowOffset + (left + tooltipRect.width - (viewportWidth - SPACING));
          left = viewportWidth - SPACING - tooltipRect.width;
        }
      }
      // Position above as last resort
      else {
        top = triggerRect.top - SPACING - tooltipRect.height - ARROW_SIZE;
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        arrowPosition = 'bottom';
        arrowOffset = tooltipRect.width / 2;
        
        // Adjust if tooltip goes off left or right
        if (left < SPACING) {
          arrowOffset = arrowOffset + left - SPACING;
          left = SPACING;
        } else if (left + tooltipRect.width > viewportWidth - SPACING) {
          arrowOffset = arrowOffset + (left + tooltipRect.width - (viewportWidth - SPACING));
          left = viewportWidth - SPACING - tooltipRect.width;
        }
      }

      setPosition({ top, left, arrowPosition, arrowOffset });
    };

    // Initial calculation
    calculatePosition();

    // Recalculate on scroll or resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isOpen, triggerRef, onClose]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current && 
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Get token counts
  const inputTokens = tokenUsage?.inputTokens || 0;
  const outputTokens = tokenUsage?.outputTokens || 0;
  const cacheCreationTokens = tokenUsage?.cacheCreationInputTokens || 0;
  const cacheReadTokens = tokenUsage?.cacheReadInputTokens || 0;

  // Get costs from backend (if available) or calculate as fallback
  const costs = tokenUsage?.costs;
  const inputCost = costs?.input_cost || 0;
  const outputCost = costs?.output_cost || 0;
  const cacheWriteCost = costs?.cache_write_cost || 0;
  const cacheReadCost = costs?.cache_read_cost || 0;
  const totalCost = costs?.total_cost || 0;

  return createPortal(
    <div
      ref={tooltipRef}
      className="fixed"
      style={{
        top: position ? `${position.top}px` : '-9999px',
        left: position ? `${position.left}px` : '-9999px',
        zIndex: 10000,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {/* Tooltip Container with Shadow */}
      <div className="relative">
        {/* Arrow */}
        {position && (
          <div
            className="absolute z-10 text-bg-primary"
            style={{
              ...(position.arrowPosition === 'left' && {
                left: '-6px',
                top: `${position.arrowOffset - 8}px`,
              }),
              ...(position.arrowPosition === 'right' && {
                right: '-6px',
                top: `${position.arrowOffset - 8}px`,
              }),
              ...(position.arrowPosition === 'top' && {
                top: '-6px',
                left: `${position.arrowOffset - 8}px`,
              }),
              ...(position.arrowPosition === 'bottom' && {
                bottom: '-6px',
                left: `${position.arrowOffset - 8}px`,
              }),
            }}
          >
            {position.arrowPosition === 'left' && (
              <svg width="6" height="16" viewBox="0 0 6 16" fill="currentColor">
                <path d="M0 8L6 0L6 16L0 8Z" fill="currentColor" />
              </svg>
            )}
            {position.arrowPosition === 'right' && (
              <svg width="6" height="16" viewBox="0 0 6 16" fill="currentColor">
                <path d="M6 8L0 0L0 16L6 8Z" fill="currentColor" />
              </svg>
            )}
            {position.arrowPosition === 'top' && (
              <svg width="16" height="6" viewBox="0 0 16 6" fill="currentColor">
                <path d="M8 0L16 6L0 6L8 0Z" fill="currentColor" />
              </svg>
            )}
            {position.arrowPosition === 'bottom' && (
              <svg width="16" height="6" viewBox="0 0 16 6" fill="currentColor">
                <path d="M8 6L16 0L0 0L8 6Z" fill="currentColor" />
              </svg>
            )}
          </div>
        )}

        {/* Tooltip Content */}
        <div 
          className="bg-bg-primary rounded-bg-v2-md shadow-[0px_24px_48px_-12px_rgba(10,13,18,0.18),0px_4px_4px_-2px_rgba(10,13,18,0.04)] border border-border-secondary"
          style={{ minWidth: '301px' }}
        >
          {/* Main Content */}
          <div className="p-bg-v2-lg flex flex-col gap-bg-v2-md">
            {/* Cost Breakdown Section */}
            <div className="p-bg-v2-md">
              <div className="flex flex-col gap-bg-v2-xxs">
                {/* Title */}
                <p className="font-bg-v2-sans font-bg-v2-semibold text-bg-v2-sm leading-[20px] text-text-primary">
                  Statistics
                </p>
                
                {/* Subtitle */}
                <div className="flex items-start pb-bg-v2-xxs">
                  <p className="font-bg-v2-sans font-bg-v2-semibold text-bg-v2-xs leading-[18px] text-text-tertiary">
                    Complete Request Cost
                  </p>
                </div>
                
                {/* Divider */}
                <div className="flex flex-col items-start px-0 py-bg-v2-xs">
                  <div className="bg-border-secondary h-[1px] w-full" />
                </div>
                
                {/* Token Costs */}
                <div className="flex gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                  <p className="font-bg-v2-sans font-bg-v2-medium text-text-primary w-[160px]">
                    Input Tokens ({inputTokens.toLocaleString()})
                  </p>
                  <p className="font-bg-v2-sans font-bg-v2-semibold text-text-primary text-right w-[99px]">
                    {formatCost(inputCost)}
                  </p>
                </div>
                
                <div className="flex gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                  <p className="font-bg-v2-sans font-bg-v2-medium text-text-primary w-[160px]">
                    Output Tokens ({outputTokens.toLocaleString()})
                  </p>
                  <p className="font-bg-v2-sans font-bg-v2-semibold text-text-primary text-right w-[99px]">
                    {formatCost(outputCost)}
                  </p>
                </div>
                
                {cacheCreationTokens > 0 && (
                  <div className="flex gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                    <p className="font-bg-v2-sans font-bg-v2-medium text-text-primary w-[160px]">
                      Cache Creation ({cacheCreationTokens.toLocaleString()})
                    </p>
                    <p className="font-bg-v2-sans font-bg-v2-semibold text-text-primary text-right w-[99px]">
                      {formatCost(cacheWriteCost)}
                    </p>
                  </div>
                )}
                
                {cacheReadTokens > 0 && (
                  <div className="flex gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                    <p className="font-bg-v2-sans font-bg-v2-medium text-text-primary w-[160px]">
                      Cache Read ({cacheReadTokens.toLocaleString()})
                    </p>
                    <p className="font-bg-v2-sans font-bg-v2-semibold text-text-primary text-right w-[99px]">
                      {formatCost(cacheReadCost)}
                    </p>
                  </div>
                )}
                
                {/* Divider */}
                <div className="flex flex-col items-start px-0 py-bg-v2-xs">
                  <div className="bg-border-secondary h-[1px] w-full" />
                </div>
                
                {/* Total Cost */}
                <div className="flex font-bg-v2-semibold gap-[2px] items-start leading-[20px] text-bg-v2-sm w-full">
                  <p className="font-bg-v2-sans text-text-primary w-[160px]">
                    Total Cost:
                  </p>
                  <p className="font-bg-v2-sans text-text-primary text-right w-[99px]">
                    {formatCost(totalCost)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Development Timing Section */}
            {developmentTiming?.duration_seconds && (
              <div className="bg-bg-brand-primary-alt px-bg-v2-md py-bg-v2-lg rounded-bg-v2-md w-full">
                <div className="flex flex-col gap-[2px]">
                  <div className="flex font-bg-v2-medium gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                    <p className="font-bg-v2-sans text-text-primary w-[160px]">
                      Time Taken:
                    </p>
                    <p className="font-bg-v2-sans text-text-primary text-right w-[99px]">
                      {formatRoundedDuration(developmentTiming.duration_seconds)}
                    </p>
                  </div>
                  <div className="flex font-bg-v2-medium gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                    <p className="font-bg-v2-sans text-text-primary w-[160px]">
                      Model Interactions:
                    </p>
                    <p className="font-bg-v2-sans text-text-primary text-right w-[99px]">
                      {developmentTiming.model_interactions || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Brainstorm Timing Section (without model interactions) */}
            {brainstormTiming?.duration_seconds && !developmentTiming?.duration_seconds && (
              <div className="bg-bg-brand-primary-alt px-bg-v2-md py-bg-v2-lg rounded-bg-v2-md w-full">
                <div className="flex flex-col gap-[2px]">
                  <div className="flex font-bg-v2-medium gap-[2px] items-start leading-[18px] text-bg-v2-xs w-full">
                    <p className="font-bg-v2-sans text-text-primary w-[160px]">
                      Time Taken:
                    </p>
                    <p className="font-bg-v2-sans text-text-primary text-right w-[99px]">
                      {formatRoundedDuration(brainstormTiming.duration_seconds)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

